window.AAEmailWorkspace = (() => {
  const state = {
    root: null,
    module: null,
    clientId: null,
    requests: [],
    activeRequest: null,
    activeFiles: []
  };

  function getElement(selector) {
    return state.module?.querySelector(selector) || null;
  }

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getStatusLabel(status) {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      in_progress: 'In Progress',
      ready: 'Ready',
      sent: 'Sent',
      archived: 'Archived'
    };

    return labels[status] || 'Draft';
  }

  function isEditableRequest(request) {
    return [
      'draft',
      'submitted'
    ].includes(request?.status);
  }

  function isInternalWorkspace() {
    return window.AAWorkspaceContext
      ?.get?.()
      ?.isInternal === true;
  }

  function formatEmailDate(value) {
    if (!value) {
      return '';
    }

    const parts =
      String(value)
        .slice(0, 10)
        .split('-')
        .map(Number);

    if (
      parts.length !== 3 ||
      !parts[0] ||
      !parts[1] ||
      !parts[2]
    ) {
      return '';
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }
    ).format(
      new Date(
        Date.UTC(
          parts[0],
          parts[1] - 1,
          parts[2]
        )
      )
    );
  }

  function showFeedback(
    message,
    type = 'success'
  ) {
    const feedback =
      getElement(
        '[data-aa-email-feedback]'
      );

    if (!feedback) {
      return;
    }

    feedback.hidden = !message;

    feedback.textContent =
      message || '';

    feedback.dataset.status =
      type;
  }

  function getUpcomingRequests() {
    return state.requests
      .filter(
        (request) =>
          ![
            'sent',
            'archived'
          ].includes(request.status)
      )
      .sort((a, b) =>
        String(a.email_date || '')
          .localeCompare(
            String(b.email_date || '')
          )
      );
  }

  function getHistoryRequests() {
    return state.requests
      .filter(
        (request) =>
          [
            'sent',
            'archived'
          ].includes(request.status)
      )
      .sort((a, b) =>
        String(b.email_date || '')
          .localeCompare(
            String(a.email_date || '')
          )
      );
  }

  function renderRequestRow(request) {
    const editable =
      isEditableRequest(request);

    const subject =
      String(
        request.subject || ''
      ).trim();

    return `
      <article class="aa-email-request">
        <div class="aa-email-request__date">
          ${escapeHtml(
            formatEmailDate(
              request.email_date
            )
          )}
        </div>

        <div class="aa-email-request__body">
          <h4>
            ${escapeHtml(
              request.email_topic ||
              'Email Request'
            )}
          </h4>

          ${
            subject
              ? `
                <p class="aa-email-request__subject">
                  ${escapeHtml(subject)}
                </p>
              `
              : ''
          }
        </div>

        <span
          class="aa-status-pill"
          data-status="${escapeHtml(
            request.status
          )}"
        >
          ${escapeHtml(
            getStatusLabel(
              request.status
            )
          )}
        </span>

        <div class="aa-email-request__actions">
          <button
            type="button"
            class="aa-button aa-button--secondary aa-button--small"
            data-aa-email-open-request="${escapeHtml(
              request.id
            )}"
          >
            ${editable ? 'Edit' : 'Open'}
          </button>

          ${
            isInternalWorkspace()
              ? `
                <button
                  type="button"
                  class="aa-email-request__delete"
                  data-aa-email-delete-request="${escapeHtml(
                    request.id
                  )}"
                >
                  Delete
                </button>
              `
              : ''
          }
        </div>
      </article>
    `;
  }

  function renderRequestLists() {
    const upcomingContainer =
      getElement(
        '[data-aa-email-upcoming]'
      );

    const historyContainer =
      getElement(
        '[data-aa-email-history]'
      );

    const upcoming =
      getUpcomingRequests();

    const history =
      getHistoryRequests();

    if (upcomingContainer) {
      if (!state.requests.length) {
        upcomingContainer.innerHTML = `
          <div class="aa-email-state">
            <p>No email requests yet.</p>

            <button
              type="button"
              class="aa-button aa-button--primary aa-button--small"
              data-aa-email-add-request
            >
              Add Email Request
            </button>
          </div>
        `;
      } else if (!upcoming.length) {
        upcomingContainer.innerHTML = `
          <div class="aa-email-state">
            No upcoming email requests.
          </div>
        `;
      } else {
        upcomingContainer.innerHTML =
          upcoming
            .map(renderRequestRow)
            .join('');
      }
    }

    if (historyContainer) {
      if (!history.length) {
        historyContainer.innerHTML = `
          <div class="aa-email-state">
            No sent or archived emails yet.
          </div>
        `;
      } else {
        historyContainer.innerHTML =
          history
            .map(renderRequestRow)
            .join('');
      }
    }
  }

  async function loadRequests() {
    if (
      !state.clientId ||
      !window.AASupabaseClient
        ?.getEmailRequests
    ) {
      return;
    }

    try {
      state.requests =
        await window.AASupabaseClient
          .getEmailRequests({
            clientId:
              state.clientId
          });

      renderRequestLists();
    } catch (error) {
      console.error(
        'Unable to load Email requests:',
        error
      );

      const upcoming =
        getElement(
          '[data-aa-email-upcoming]'
        );

      const history =
        getElement(
          '[data-aa-email-history]'
        );

      if (upcoming) {
        upcoming.innerHTML = `
          <div class="aa-email-state">
            We couldn't load your email requests right now.
          </div>
        `;
      }

      if (history) {
        history.innerHTML = '';
      }
    }
  }

  function setFormReadOnly(readOnly) {
    [
      '[data-aa-email-date]',
      '[data-aa-email-topic]',
      '[data-aa-email-subject]',
      '[data-aa-email-preheader]',
      '[data-aa-email-notes]',
      '[data-aa-email-materials]'
    ].forEach((selector) => {
      const field =
        getElement(selector);

      if (field) {
        field.disabled =
          readOnly;
      }
    });
  }

  function configureFormActions(
    request = null
  ) {
    const saveDraft =
      getElement(
        '[data-aa-email-save-status="draft"]'
      );

    const submit =
      getElement(
        '[data-aa-email-save-status="submitted"]'
      );

    const saveChanges =
      getElement(
        '[data-aa-email-save-status="preserve"]'
      );

    const archive =
      getElement(
        '[data-aa-email-archive]'
      );

    const restore =
      getElement(
        '[data-aa-email-restore]'
      );

    if (!request) {
      if (saveDraft) {
        saveDraft.hidden = false;
      }

      if (submit) {
        submit.hidden = false;
      }

      if (saveChanges) {
        saveChanges.hidden = true;
      }

      if (archive) {
        archive.hidden = true;
      }

      if (restore) {
        restore.hidden = true;
      }

      return;
    }

    const editable =
      isEditableRequest(request);

    if (!editable) {
      if (saveDraft) {
        saveDraft.hidden = true;
      }

      if (submit) {
        submit.hidden = true;
      }

      if (saveChanges) {
        saveChanges.hidden = true;
      }

      if (archive) {
        archive.hidden = true;
      }

      if (restore) {
        restore.hidden =
          request.status !== 'archived';
      }

      return;
    }

    if (restore) {
      restore.hidden = true;
    }

    if (request.status === 'draft') {
      if (saveDraft) {
        saveDraft.hidden = false;
      }

      if (submit) {
        submit.hidden = false;
      }

      if (saveChanges) {
        saveChanges.hidden = true;
      }
    } else {
      if (saveDraft) {
        saveDraft.hidden = true;
      }

      if (submit) {
        submit.hidden = true;
      }

      if (saveChanges) {
        saveChanges.hidden = false;
      }
    }

    if (archive) {
      archive.hidden = false;
    }
  }

  function openNewRequest() {
    const editor =
      getElement(
        '[data-aa-email-editor]'
      );

    const form =
      getElement(
        '[data-aa-email-request-form]'
      );

    const title =
      getElement(
        '[data-aa-email-editor-title]'
      );

    if (!editor || !form) {
      return;
    }

    state.activeRequest = null;
    state.activeFiles = [];

    form.reset();

    getElement(
      '[data-aa-email-request-id]'
    ).value = '';

    if (title) {
      title.textContent =
        'Add Email Request';
    }

    setFormReadOnly(false);
    configureFormActions(null);
    renderFiles();

    showFeedback('');

    editor.hidden = false;

    editor.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  function closeEditor() {
    const editor =
      getElement(
        '[data-aa-email-editor]'
      );

    const form =
      getElement(
        '[data-aa-email-request-form]'
      );

    if (form) {
      form.reset();
    }

    state.activeRequest = null;
    state.activeFiles = [];

    if (editor) {
      editor.hidden = true;
    }
  }

  function renderFiles() {
    const container =
      getElement(
        '[data-aa-email-files]'
      );

    if (!container) {
      return;
    }

    if (!state.activeFiles.length) {
      container.innerHTML = `
        <div class="aa-email-files__empty">
          No materials uploaded yet.
        </div>
      `;

      return;
    }

    const editable =
      isEditableRequest(
        state.activeRequest
      );

    container.innerHTML =
      state.activeFiles
        .map((file) => `
          <div class="aa-email-file">
            <span class="aa-email-file__name">
              ${escapeHtml(
                file.file_name
              )}
            </span>

            <div class="aa-email-file__actions">
              <button
                type="button"
                class="aa-button aa-button--secondary aa-button--small"
                data-aa-email-view-file="${escapeHtml(
                  file.id
                )}"
              >
                View
              </button>

              ${
                editable
                  ? `
                    <button
                      type="button"
                      class="aa-button aa-button--secondary aa-button--small"
                      data-aa-email-remove-file="${escapeHtml(
                        file.id
                      )}"
                    >
                      Remove
                    </button>
                  `
                  : ''
              }
            </div>
          </div>
        `)
        .join('');
  }

  async function loadFiles(
    requestId
  ) {
    state.activeFiles = [];

    if (
      !requestId ||
      !window.AASupabaseClient
        ?.getEmailRequestFiles
    ) {
      renderFiles();
      return;
    }

    try {
      state.activeFiles =
        await window.AASupabaseClient
          .getEmailRequestFiles({
            requestId
          });

      renderFiles();
    } catch (error) {
      console.error(
        'Unable to load Email request files:',
        error
      );

      renderFiles();

      showFeedback(
        'The request loaded, but its materials could not be loaded.',
        'error'
      );
    }
  }

  async function openRequest(
    requestId
  ) {
    if (
      !requestId ||
      !window.AASupabaseClient
        ?.getEmailRequest
    ) {
      return;
    }

    try {
      const request =
        await window.AASupabaseClient
          .getEmailRequest({
            id: requestId
          });

      if (!request) {
        throw new Error(
          'Email request was not found.'
        );
      }

      state.activeRequest =
        request;

      const editor =
        getElement(
          '[data-aa-email-editor]'
        );

      const title =
        getElement(
          '[data-aa-email-editor-title]'
        );

      getElement(
        '[data-aa-email-request-id]'
      ).value =
        request.id || '';

      getElement(
        '[data-aa-email-date]'
      ).value =
        request.email_date || '';

      getElement(
        '[data-aa-email-topic]'
      ).value =
        request.email_topic || '';

      getElement(
        '[data-aa-email-subject]'
      ).value =
        request.subject || '';

      getElement(
        '[data-aa-email-preheader]'
      ).value =
        request.preheader || '';

      getElement(
        '[data-aa-email-notes]'
      ).value =
        request.notes || '';

      if (title) {
        title.textContent =
          request.email_topic ||
          'Email Request';
      }

      const editable =
        isEditableRequest(request);

      setFormReadOnly(
        !editable
      );

      configureFormActions(
        request
      );

      await loadFiles(
        request.id
      );

      showFeedback('');

      if (editor) {
        editor.hidden = false;

        editor.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } catch (error) {
      console.error(
        'Unable to open Email request:',
        error
      );

      showFeedback(
        'We could not open that email request.',
        'error'
      );
    }
  }

  function setSaveButtonsBusy(
    busy
  ) {
    state.module
      ?.querySelectorAll(
        '[data-aa-email-save-status]'
      )
      .forEach((button) => {
        button.disabled =
          busy;
      });
  }

  async function saveRequest(
    event
  ) {
    event.preventDefault();

    const form =
      event.currentTarget;

    if (!form.reportValidity()) {
      return;
    }

    const submitter =
      event.submitter;

    const requestedStatus =
      submitter?.dataset
        ?.aaEmailSaveStatus ||
      'draft';

    const existing =
      state.activeRequest;

    const status =
      requestedStatus === 'preserve'
        ? existing?.status || 'draft'
        : requestedStatus;

    const emailDate =
      getElement(
        '[data-aa-email-date]'
      )?.value;

    const emailTopic =
      getElement(
        '[data-aa-email-topic]'
      )?.value?.trim();

    const subject =
      getElement(
        '[data-aa-email-subject]'
      )?.value?.trim();

    const preheader =
      getElement(
        '[data-aa-email-preheader]'
      )?.value?.trim();

    const notes =
      getElement(
        '[data-aa-email-notes]'
      )?.value?.trim();

    const materials =
      Array.from(
        getElement(
          '[data-aa-email-materials]'
        )?.files || []
      );

    setSaveButtonsBusy(true);

    try {
      let request;

      if (existing?.id) {
        request =
          await window.AASupabaseClient
            .updateEmailRequest({
              id: existing.id,
              updates: {
                emailDate,
                emailTopic,
                subject,
                preheader,
                notes,
                status
              }
            });
      } else {
        request =
          await window.AASupabaseClient
            .createEmailRequest({
              clientId:
                state.clientId,
              emailDate,
              emailTopic,
              subject,
              preheader,
              notes,
              status
            });
      }

      let uploadError = null;

      for (
        const file
        of materials
      ) {
        try {
          await window.AASupabaseClient
            .uploadEmailRequestFile({
              clientId:
                state.clientId,
              requestId:
                request.id,
              file
            });
        } catch (error) {
          uploadError =
            uploadError || error;

          console.error(
            'Unable to upload Email material:',
            error
          );
        }
      }

      await loadRequests();

      if (uploadError) {
        await openRequest(
          request.id
        );

        showFeedback(
          'The email request was saved, but one or more materials could not be uploaded.',
          'error'
        );

        return;
      }

      closeEditor();

      showFeedback(
        status === 'submitted'
          ? 'Email request submitted.'
          : existing
            ? 'Email request updated.'
            : 'Email draft saved.'
      );
    } catch (error) {
      console.error(
        'Unable to save Email request:',
        error
      );

      showFeedback(
        'We could not save this email request. Please try again.',
        'error'
      );
    } finally {
      setSaveButtonsBusy(false);
    }
  }

  function showArchiveConfirmation() {
    const confirmation =
      getElement(
        '[data-aa-email-archive-confirm]'
      );

    if (!confirmation) {
      return;
    }

    confirmation.hidden = false;

    confirmation.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  function hideArchiveConfirmation() {
    const confirmation =
      getElement(
        '[data-aa-email-archive-confirm]'
      );

    if (confirmation) {
      confirmation.hidden = true;
    }
  }

  async function archiveRequest() {
    const request =
      state.activeRequest;

    if (!request?.id) {
      return;
    }

    try {
      await window.AASupabaseClient
        .archiveEmailRequest({
          id: request.id
        });

      hideArchiveConfirmation();
      closeEditor();

      await loadRequests();

      showFeedback(
        'Email request archived.'
      );
    } catch (error) {
      console.error(
        'Unable to archive Email request:',
        error
      );

      showFeedback(
        'We could not archive this email request.',
        'error'
      );
    }
  }


  async function restoreRequest() {
    const request =
      state.activeRequest;

    if (
      !request?.id ||
      request.status !== 'archived'
    ) {
      return;
    }

    try {
      await window.AASupabaseClient
        .updateEmailRequest({
          id: request.id,
          updates: {
            status: 'submitted'
          }
        });

      closeEditor();

      await loadRequests();

      showFeedback(
        'Email request restored to Upcoming Emails.'
      );
    } catch (error) {
      console.error(
        'Unable to restore Email request:',
        error
      );

      showFeedback(
        'We could not restore this email request.',
        'error'
      );
    }
  }


  async function viewFile(
    fileId
  ) {
    const file =
      state.activeFiles.find(
        (item) =>
          item.id === fileId
      );

    if (!file) {
      return;
    }

    try {
      const signedUrl =
        await window.AASupabaseClient
          .getEmailRequestFileSignedUrl({
            path:
              file.storage_path
          });

      window.open(
        signedUrl,
        '_blank',
        'noopener,noreferrer'
      );
    } catch (error) {
      console.error(
        'Unable to open Email material:',
        error
      );

      showFeedback(
        'We could not open that material.',
        'error'
      );
    }
  }

  async function removeFile(
    fileId
  ) {
    const file =
      state.activeFiles.find(
        (item) =>
          item.id === fileId
      );

    if (!file) {
      return;
    }

    try {
      await window.AASupabaseClient
        .removeEmailRequestFile({
          id: file.id
        });

      await loadFiles(
        state.activeRequest?.id
      );

      showFeedback(
        'Material removed.'
      );
    } catch (error) {
      console.error(
        'Unable to remove Email material:',
        error
      );

      showFeedback(
        'We could not remove that material.',
        'error'
      );
    }
  }

  async function deleteRequest(
    requestId,
    button
  ) {
    if (
      !requestId ||
      !isInternalWorkspace() ||
      !window.AASupabaseClient
        ?.deleteEmailRequest
    ) {
      return;
    }

    const confirming =
      button?.dataset
        ?.confirming === 'true';

    if (!confirming) {
      if (button) {
        button.dataset.confirming =
          'true';

        button.textContent =
          'Confirm Delete';

        window.setTimeout(
          () => {
            if (
              button.isConnected &&
              button.dataset
                .confirming === 'true'
            ) {
              delete button.dataset
                .confirming;

              button.textContent =
                'Delete';
            }
          },
          5000
        );
      }

      return;
    }

    if (button) {
      button.disabled = true;
      button.textContent =
        'Deleting...';
    }

    try {
      await window.AASupabaseClient
        .deleteEmailRequest({
          id: requestId
        });

      if (
        state.activeRequest?.id ===
        requestId
      ) {
        closeEditor();
      }

      await loadRequests();

      showFeedback(
        'Email request deleted.'
      );
    } catch (error) {
      console.error(
        'Unable to delete Email request:',
        error
      );

      if (button) {
        button.disabled = false;
        delete button.dataset
          .confirming;
        button.textContent =
          'Delete';
      }

      showFeedback(
        'We could not delete this email request.',
        'error'
      );
    }
  }

  function bindEvents() {
    if (
      !state.module ||
      state.module.dataset
        .aaEmailEventsBound ===
        'true'
    ) {
      return;
    }

    state.module.dataset
      .aaEmailEventsBound =
      'true';

    state.module.addEventListener(
  'click',
  async (event) => {
        const addButton =
          event.target.closest(
            '[data-aa-email-add-request]'
          );

        if (addButton) {
          openNewRequest();
          return;
        }

        const cancelButton =
          event.target.closest(
            '[data-aa-email-cancel]'
          );

        if (cancelButton) {
          closeEditor();
          return;
        }

        const deleteButton =
          event.target.closest(
            '[data-aa-email-delete-request]'
          );

        if (deleteButton) {
          await deleteRequest(
            deleteButton.dataset
              .aaEmailDeleteRequest,
            deleteButton
          );

          return;
        }

        const openButton =
          event.target.closest(
            '[data-aa-email-open-request]'
          );

        if (openButton) {
          await openRequest(
            openButton.dataset
              .aaEmailOpenRequest
          );

          return;
        }

        const archiveButton =
          event.target.closest(
            '[data-aa-email-archive]'
          );

        if (archiveButton) {
          showArchiveConfirmation();
          return;
        }

        const archiveCancel =
          event.target.closest(
            '[data-aa-email-archive-cancel]'
          );

        if (archiveCancel) {
          hideArchiveConfirmation();
          return;
        }

        const archiveConfirm =
          event.target.closest(
            '[data-aa-email-archive-confirm-button]'
          );

        if (archiveConfirm) {
          await archiveRequest();
          return;
        }

        const restoreButton =
          event.target.closest(
            '[data-aa-email-restore]'
          );

        if (restoreButton) {
          await restoreRequest();
          return;
        }

        const viewButton =
          event.target.closest(
            '[data-aa-email-view-file]'
          );

        if (viewButton) {
          await viewFile(
            viewButton.dataset
              .aaEmailViewFile
          );

          return;
        }

        const removeButton =
          event.target.closest(
            '[data-aa-email-remove-file]'
          );

        if (removeButton) {
          await removeFile(
            removeButton.dataset
              .aaEmailRemoveFile
          );
        }
      }
    );

    const form =
      getElement(
        '[data-aa-email-request-form]'
      );

    if (form) {
      form.addEventListener(
        'submit',
        saveRequest
      );
    }
  }

  async function init({
    root,
    clientId,
    enabled
  } = {}) {
    const module =
      root?.querySelector(
        '[data-aa-email-module]'
      );

    if (
      !module ||
      !clientId ||
      !enabled
    ) {
      return;
    }

    state.root = root;
    state.module = module;
    state.clientId = clientId;

    bindEvents();

    await loadRequests();
  }

  return {
    init
  };
})();